// Portable Canvas2D renderer. Runs in browsers (export, thumbnails, fallback) and in Node via
// @napi-rs/canvas (headless screenshots for AI agents). The interactive editor uses the GPU renderer.

import type { Document } from './document.ts';
import type { Color, Effect, NodeData, NodeId, Paint, Rect, ShaderSource, Vec2 } from './types.ts';
import { hasDrawStroke, drawStrokeGeometry, drawStrokeReach, seedOf, type DrawStroke } from './draw.ts';
import { type Mat, type PathCmd, IDENTITY, dashOutline, lineSegment, mul, invert, localTransform, nodeOutline, intersects, expandRect, transformRect, flattenPath, sideWeights, sideStrokeRing } from './geometry.ts';
import { colorToCss, imageFilterCss, isGradient, blendCss } from './util.ts';
import type { ShapedGlyph, ShapedText, TextShaper } from './shaper.ts';
import { RTL_CHARS, paragraphLevel } from './bidi.ts';
import { layoutNodeText, textPadding, textBox, UNDERLINE_OFFSET, UNDERLINE_THICKNESS, bulletShape, tabularCell, smallCapsRuns, measureString, shapeRun, getTextShaper, codePointsBefore, withFallbacks, type GlyphFeatures, type TextLayout } from './text.ts';
import { layoutTextOnPath } from './textpath.ts';
import { connectorGeometry } from './layout.ts';

// Minimal structural type so both DOM and node canvases fit.
export type Ctx2D = CanvasRenderingContext2D;

/** One shader run: a fill generates width × height pixels; an effect transforms `input`. */
export interface ShaderRequest extends ShaderSource {
  kind: 'fill' | 'effect';
  nodeId: NodeId;
  /** Which shader of the layer (paint or effect index). */
  index: number;
  /** Output size in device pixels. */
  width: number;
  height: number;
  /** Effects: the layer's pixels (premultiplied), width × height. */
  input?: { getContext(type: '2d'): unknown; width: number; height: number };
}

/**
 * Runs shaders (the app does it on the GPU, off the main thread). Results arrive later: return
 * the latest finished image for this layer (even one made for older inputs) or null for none yet.
 */
export interface ShaderProvider {
  get(req: ShaderRequest): CanvasImageSource | null;
}

export interface ImageProvider {
  /** Return a drawable image if loaded; start loading otherwise. Videos also get the layer, which owns playback. */
  get(src: string, nodeId?: NodeId): CanvasImageSource | undefined;
}

export interface RenderOptions {
  /** Page-space rect that is visible (for culling). Omit to draw everything. */
  viewport?: Rect;
  /** Base transform (page -> device pixels). */
  transform?: Mat;
  images?: ImageProvider;
  /** Shader fills and effects (without one, fills show a placeholder and effects pass through). */
  shaders?: ShaderProvider;
  /** Moving media for prototypes: playing videos and animated GIF frames (else the still image is used). */
  media?: ImageProvider;
  /** Offscreen canvases for blending groups (headless renderers pass their own). */
  createCanvas?: (w: number, h: number) => { getContext(type: '2d'): unknown } | null;
  /** Skip these nodes (e.g. text being edited in an overlay). */
  hidden?: Set<NodeId>;
  /** Draw page background. */
  background?: boolean;
  /** Nodes smaller than this many device px are drawn as simple boxes. */
  lodThreshold?: number;
  /**
   * The canvas holds only this subtree (GPU renderers raster layers on their own and composite
   * backdrop effects themselves): background blur and glass draw a near-invisible mask of the
   * shape instead of reading what's behind, and glass keeps its edge light.
   */
  isolated?: boolean;
}

function setTransform(ctx: Ctx2D, m: Mat) {
  ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
}

export function tracePath(ctx: Ctx2D | Path2D, cmds: PathCmd[]) {
  for (const c of cmds) {
    if (c.c === 'M') ctx.moveTo(c.x, c.y);
    else if (c.c === 'L') ctx.lineTo(c.x, c.y);
    else if (c.c === 'C') ctx.bezierCurveTo(c.x1, c.y1, c.x2, c.y2, c.x, c.y);
    else ctx.closePath();
  }
}

function paintStyle(ctx: Ctx2D, p: Paint, w: number, h: number): string | CanvasGradient | null {
  if (p.type === 'SOLID') return colorToCss(p.color, p.opacity ?? 1);
  if (isGradient(p)) {
    const from = p.from ?? { x: 0.5, y: 0 };
    const to = p.to ?? { x: 0.5, y: 1 };
    let g: CanvasGradient;
    let stops = p.stops;
    if (p.type === 'GRADIENT_LINEAR') g = ctx.createLinearGradient(from.x * w, from.y * h, to.x * w, to.y * h);
    else if (p.type === 'GRADIENT_ANGULAR' && 'createConicGradient' in ctx) {
      // sweeps clockwise around `from`, starting in the direction of `to`
      const start = Math.atan2((to.y - from.y) * h, (to.x - from.x) * w);
      g = (ctx as CanvasRenderingContext2D).createConicGradient(conicFixedStart ? 0 : start, from.x * w, from.y * h);
      // Skia's sweep always starts at 12 o'clock: turn the stops instead
      if (conicFixedStart) stops = rotateStops(p.stops, (((start + Math.PI / 2) / (Math.PI * 2)) % 1 + 1) % 1);
    } else {
      const r = Math.hypot((to.x - from.x) * w, (to.y - from.y) * h);
      g = ctx.createRadialGradient(from.x * w, from.y * h, 0, from.x * w, from.y * h, Math.max(1, r));
    }
    for (const s of stops) g.addColorStop(Math.max(0, Math.min(1, s.position)), colorToCss(s.color, p.opacity ?? 1));
    return g;
  }
  return null;
}

/**
 * Diamond gradient, drawn exactly: its levels are straight lines in each quadrant around the
 * center, so each quadrant is a linear gradient along the diagonal, clipped to the shape.
 */
function fillDiamond(ctx: Ctx2D, p: Extract<Paint, { stops: unknown }>, outline: PathCmd[], rule: CanvasFillRule, w: number, h: number) {
  const from = p.from ?? { x: 0.5, y: 0.5 };
  const to = p.to ?? { x: 1, y: 0.5 };
  const cx = from.x * w,
    cy = from.y * h;
  // the diamond's two half-axes, as Figma's handles give them: along `to`, and the perpendicular
  // handle (the same length in the layer's unit box, so the diamond stretches with the box)
  const du = { x: (to.x - from.x) * w, y: (to.y - from.y) * h };
  const dv = { x: -(to.y - from.y) * w, y: (to.x - from.x) * h };
  const det = du.x * dv.y - dv.x * du.y;
  if (Math.abs(det) < 1e-9) return;
  const far = (Math.max(w, h) + Math.hypot(du.x, du.y) + Math.hypot(dv.x, dv.y)) * 4;
  ctx.save();
  ctx.beginPath();
  tracePath(ctx, outline);
  ctx.clip(rule);
  for (const [sx, sy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]]) {
    // in this quadrant t = a + b for (x, y) = c + a·du·sx + b·dv·sy: linear, so one linear gradient
    // along its gradient vector g = M⁻ᵀ(1, 1)
    const gx = (sx * dv.y - sy * du.y) / det,
      gy = (-sx * dv.x + sy * du.x) / det;
    const gl = gx * gx + gy * gy || 1;
    const g = ctx.createLinearGradient(cx, cy, cx + gx / gl, cy + gy / gl);
    for (const s of p.stops) g.addColorStop(Math.max(0, Math.min(1, s.position)), colorToCss(s.color, p.opacity ?? 1));
    ctx.fillStyle = g;
    // the quadrant: the cone between ±du and ±dv
    const ul = Math.hypot(du.x, du.y) || 1,
      vl = Math.hypot(dv.x, dv.y) || 1;
    const ax = ((sx * du.x) / ul) * far,
      ay = ((sx * du.y) / ul) * far,
      bx = ((sy * dv.x) / vl) * far,
      by = ((sy * dv.y) / vl) * far;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + ax, cy + ay);
    ctx.lineTo(cx + ax + bx, cy + ay + by);
    ctx.lineTo(cx + bx, cy + by);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

function deviceScale(m: Mat): number {
  return Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
}

/**
 * Expanded page-space bounds including strokes and shadows (for culling). `exact` counts strokes only
 * as far as they reach (none for inside strokes, half for centered ones), as exports measure them.
 */
export function visualBounds(doc: Document, n: NodeData, exact = false): Rect {
  let b = doc.worldBounds(n.id);
  const reach = !exact ? n.strokeWeight : !n.strokes.some((p) => p.visible !== false) ? 0 : n.strokeAlign === 'INSIDE' && n.type !== 'LINE' ? 0 : n.strokeAlign === 'OUTSIDE' ? n.strokeWeight : n.strokeWeight / 2;
  let pad = n.strokes.length ? (hasDrawStroke(n) ? Math.max(n.strokeWeight, drawStrokeReach(n)) : reach) : 0;
  for (const e of n.effects) {
    if (e.visible === false) continue;
    if (e.type === 'TEXTURE') pad = Math.max(pad, (e.radius ?? 4) * 2);
    if (e.type === 'DROP_SHADOW') pad = Math.max(pad, e.radius * 2 + Math.abs(e.offset.x) + Math.abs(e.offset.y) + (e.spread ?? 0));
    if (e.type === 'LAYER_BLUR') pad = Math.max(pad, e.radius * 2);
  }
  if (n.type === 'CONNECTOR' || n.type === 'LINE') pad += 16;
  if (n.textPath) pad += (n.fontSize ?? 16) * 1.4; // glyphs stand off the path
  if (pad) b = expandRect(b, pad);
  return b;
}

export class Renderer2D {
  constructor(readonly doc: Document) {}
  /** Groups being drawn into their offscreen layer (their blend applies when compositing). */
  private flattening = new Set<NodeId>();

  /** Render a page (or any container's children) into ctx. */
  renderPage(ctx: Ctx2D, pageId: NodeId, opts: RenderOptions = {}) {
    const page = this.doc.must(pageId);
    const base = opts.transform ?? IDENTITY;
    if (opts.background !== false && page.backgroundColor) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = colorToCss(page.backgroundColor);
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.restore();
    }
    this.renderChildren(ctx, pageId, base, opts, 1);
  }

  /** Draw children in order; a mask layer masks the siblings above it (up to the next mask). */
  private renderChildren(ctx: Ctx2D, parentId: NodeId, m: Mat, opts: RenderOptions, alpha: number) {
    const kids = this.doc.children(parentId);
    let masked = false;
    for (let i = 0; i < kids.length; i++) {
      const c = kids[i];
      if (c.isMask && c.visible) {
        if (masked) ctx.restore();
        masked = false;
        if (alphaMask(c)) {
          let end = i + 1;
          while (end < kids.length && !(kids[end].isMask && kids[end].visible)) end++;
          if (this.renderAlphaMasked(ctx, parentId, c, kids.slice(i + 1, end), m, opts, alpha)) {
            i = end - 1;
            continue;
          }
        }
        // an opaque mask keeps exactly its outline: clip to it
        ctx.save();
        masked = true;
        setTransform(ctx, mul(m, localTransform(c)));
        ctx.beginPath();
        tracePath(ctx, nodeOutline(c) ?? []);
        ctx.clip(c.windingRule === 'EVENODD' ? 'evenodd' : 'nonzero');
        continue;
      }
      this.renderNode(ctx, c, m, opts, alpha);
    }
    if (masked) ctx.restore();
  }

  /**
   * Layers under an alpha mask (Figma's default mask type): drawn offscreen, then kept only as far as
   * the mask layer's own pixels are opaque (its fills, gradient stops, strokes, effects and opacity).
   * Offscreen means just the device box the mask covers, since nothing shows outside it. False when
   * there's no canvas to draw offscreen on (then the caller clips to the outline).
   */
  private renderAlphaMasked(ctx: Ctx2D, parentId: NodeId, mask: NodeData, layers: NodeData[], m: Mat, opts: RenderOptions, alpha: number): boolean {
    const W = ctx.canvas?.width,
      H = ctx.canvas?.height;
    if (!W || !H) return false;
    // device ← page is the parent's device transform after undoing its world transform
    const box = transformRect(mul(m, invert(this.doc.worldTransform(parentId))), visualBounds(this.doc, mask));
    const x0 = Math.max(0, Math.floor(box.x)),
      y0 = Math.max(0, Math.floor(box.y)),
      x1 = Math.min(W, Math.ceil(box.x + box.width)),
      y1 = Math.min(H, Math.ceil(box.y + box.height));
    if (x1 <= x0 || y1 <= y0) return true; // the mask is off this canvas: nothing it masks shows
    const make = opts.createCanvas ?? defaultCanvas;
    const content = make(x1 - x0, y1 - y0),
      shape = make(x1 - x0, y1 - y0);
    const cctx = content?.getContext('2d') as Ctx2D | null | undefined,
      sctx = shape?.getContext('2d') as Ctx2D | null | undefined;
    if (!content || !shape || !cctx || !sctx) return false;
    const shifted = mul([1, 0, 0, 1, -x0, -y0], m);
    for (const l of layers) this.renderNode(cctx, l, shifted, opts, alpha);
    // the mask as a layer of its own (not with the opacity it inherits: the content has that already)
    this.renderNode(sctx, mask, shifted, opts, 1);
    cctx.save();
    cctx.setTransform(1, 0, 0, 1, 0, 0);
    cctx.globalCompositeOperation = 'destination-in';
    cctx.drawImage(shape as unknown as CanvasImageSource, 0, 0);
    cctx.restore();
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(content as unknown as CanvasImageSource, x0, y0);
    ctx.restore();
    return true;
  }

  /** Render a single node subtree with its own world transform composed onto base. */
  renderSubtree(ctx: Ctx2D, id: NodeId, base: Mat, opts: RenderOptions = {}) {
    const n = this.doc.must(id);
    const parentWorld = n.parent ? this.doc.worldTransform(n.parent) : IDENTITY;
    this.renderNode(ctx, n, mul(base, parentWorld), opts, 1);
  }

  private renderNode(ctx: Ctx2D, n: NodeData, parentM: Mat, opts: RenderOptions, alpha: number) {
    if (!n.visible || opts.hidden?.has(n.id)) return;
    if (n.opacity <= 0) return;
    const m = mul(parentM, localTransform(n));
    const isContainer = n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE' || n.type === 'GROUP' || n.type === 'SECTION';
    const clips = (n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE') && n.clipsContent;

    if (opts.viewport && n.type !== 'GROUP') {
      const vb = visualBounds(this.doc, n);
      const visible = intersects(vb, opts.viewport);
      if (!visible && (!isContainer || clips)) return;
    }
    // a container being flattened draws at full opacity; its opacity applies when it's composited
    const a = this.flattening.has(n.id) ? alpha : alpha * n.opacity;
    const blend = blendCss(n.blendMode);
    // a group with opacity fades as one layer (overlapping children don't show through each other)
    const groupFade = isContainer && n.opacity < 1 && this.doc.children(n.id).length > 0;
    if ((blend || groupFade) && isContainer && !this.flattening.has(n.id) && ctx.canvas?.width) {
      // a group blends (or fades) as one flattened layer: draw it offscreen, then composite onto what's below
      const w = ctx.canvas.width,
        h = ctx.canvas.height;
      const layer = (opts.createCanvas ?? defaultCanvas)(w, h);
      const lctx = layer?.getContext('2d') as Ctx2D | null | undefined;
      if (layer && lctx) {
        this.flattening.add(n.id);
        try {
          this.renderNode(lctx, n, parentM, opts, alpha);
        } finally {
          this.flattening.delete(n.id);
        }
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        if (blend) ctx.globalCompositeOperation = blend;
        ctx.globalAlpha = n.opacity;
        ctx.drawImage(layer as unknown as CanvasImageSource, 0, 0);
        ctx.restore();
        return;
      }
    }
    // texture and progressive blur work on the drawn pixels: draw the layer offscreen, then process it
    const post = n.effects.filter((e) => e.visible !== false && (e.type === 'TEXTURE' || e.type === 'SHADER' || (e.type === 'LAYER_BLUR' && e.blurType === 'PROGRESSIVE')));
    if (post.length && !this.postFor.has(n.id) && ctx.canvas?.width) {
      if (this.postProcess(ctx, n, parentM, opts, alpha, post)) return;
    }
    const blur = n.effects.find((e) => e.type === 'LAYER_BLUR' && e.visible !== false && e.blurType !== 'PROGRESSIVE');
    ctx.save();
    // a layer's blend mode applies to everything it draws (groups being flattened draw normally)
    if (blend && !this.flattening.has(n.id)) ctx.globalCompositeOperation = blend;
    if (blur && 'filter' in ctx) ctx.filter = `blur(${(blur as { radius: number }).radius * deviceScale(m) / 2}px)`;

    // LOD: tiny nodes become a flat box (huge win when zoomed far out).
    const scale = deviceScale(m);
    const lod = opts.lodThreshold ?? 0;
    if (lod && n.type !== 'GROUP' && n.width * scale < lod && n.height * scale < lod && n.type !== 'CONNECTOR') {
      const f = n.fills.find((p) => p.type === 'SOLID' && p.visible !== false) as Extract<Paint, { type: 'SOLID' }> | undefined;
      if (f || n.type === 'TEXT') {
        setTransform(ctx, m);
        ctx.globalAlpha = a * (n.type === 'TEXT' ? 0.4 : 1);
        ctx.fillStyle = f ? colorToCss(f.color) : '#000';
        ctx.fillRect(0, 0, n.width, n.height);
      }
      ctx.restore();
      return;
    }

    ctx.globalAlpha = a;
    switch (n.type) {
      case 'TEXT':
        setTransform(ctx, m);
        this.drawShadows(ctx, n, m, () => this.drawText(ctx, n, layoutNodeText(n), n.fills));
        this.drawText(ctx, n, layoutNodeText(n), n.fills);
        break;
      case 'CONNECTOR':
        this.drawConnector(ctx, n, parentM);
        break;
      case 'LINE':
        setTransform(ctx, m);
        this.drawLine(ctx, n);
        break;
      case 'GROUP':
        break;
      default: {
        setTransform(ctx, m);
        const outline = nodeOutline(n) ?? [];
        this.drawShadows(ctx, n, m, () => {
          ctx.beginPath();
          tracePath(ctx, outline);
          ctx.fillStyle = '#000';
          ctx.fill(n.windingRule === 'EVENODD' ? 'evenodd' : 'nonzero');
        });
        this.drawBackdrop(ctx, n, m, outline, opts);
        this.drawGlass(ctx, n, m, outline, opts);
        this.fillOutline(ctx, n, outline, opts, m);
        this.drawInnerShadows(ctx, n, m, outline);
        if (n.type === 'STICKY' || n.type === 'SHAPE_WITH_TEXT') {
          const color = n.textColor ? [{ type: 'SOLID', color: n.textColor } as Paint] : [];
          ctx.save();
          const box = textBox(n);
          ctx.translate(box.x, box.y);
          this.drawText(ctx, n, layoutNodeText(n), color);
          ctx.restore();
          if (n.type === 'STICKY' && n.authorName) {
            ctx.fillStyle = 'rgba(0,0,0,0.45)';
            ctx.font = `400 12px Inter, sans-serif`;
            ctx.textBaseline = 'alphabetic';
            fillTextRun(ctx, n.authorName, box.x, n.height - 12, ctx.font);
          }
        }
        if (!isContainer || n.type === 'SECTION') this.strokeOutline(ctx, n, outline);
        if (!isContainer) this.drawNoise(ctx, n, m, outline, opts);
      }
    }

    if (isContainer) {
      ctx.restore();
      ctx.save();
      if (clips) {
        setTransform(ctx, m);
        ctx.beginPath();
        tracePath(ctx, nodeOutline(n) ?? []);
        ctx.clip();
      }
      this.renderChildren(ctx, n.id, m, opts, a);
      ctx.restore();
      if (n.type !== 'GROUP' && n.type !== 'SECTION' && n.strokes.length) {
        ctx.save();
        ctx.globalAlpha = a;
        setTransform(ctx, m);
        this.strokeOutline(ctx, n, nodeOutline(n) ?? []);
        ctx.restore();
      }
      if (n.type !== 'GROUP' && n.effects.some((e) => e.type === 'NOISE' && e.visible !== false)) {
        ctx.save();
        ctx.globalAlpha = a;
        setTransform(ctx, m);
        this.drawNoise(ctx, n, m, nodeOutline(n) ?? [], opts);
        ctx.restore();
      }
      return;
    }
    ctx.restore();
  }

  private drawShadows(ctx: Ctx2D, n: NodeData, m: Mat, draw: () => void) {
    const shadows = n.effects.filter((e) => e.type === 'DROP_SHADOW' && e.visible !== false);
    if (!shadows.length) return;
    const s = deviceScale(m);
    for (const e of shadows) {
      if (e.type !== 'DROP_SHADOW') continue;
      ctx.save();
      // Draw the shape far off-screen and offset its shadow back, so only the shadow lands.
      const FAR = 100000;
      setTransform(ctx, [m[0], m[1], m[2], m[3], m[4] - FAR, m[5]]);
      ctx.shadowColor = colorToCss(e.color);
      ctx.shadowBlur = e.radius * s;
      ctx.shadowOffsetX = e.offset.x * s + FAR;
      ctx.shadowOffsetY = e.offset.y * s;
      draw();
      ctx.restore();
    }
  }

  private fillOutline(ctx: Ctx2D, n: NodeData, outline: PathCmd[], opts: RenderOptions, m: Mat = IDENTITY) {
    const rule = n.windingRule === 'EVENODD' ? 'evenodd' : 'nonzero';
    for (const p of n.fills) {
      if (p.visible === false) continue;
      if (p.type === 'PATTERN') {
        this.fillPattern(ctx, n, p, outline, rule, opts, m);
        continue;
      }
      if (p.type === 'SHADER') {
        this.fillShader(ctx, n, p, n.fills.indexOf(p), outline, rule, opts, m);
        continue;
      }
      if (p.type === 'IMAGE') {
        const img = (opts.media && (p.video ? opts.media.get(p.video, n.id) : /\.gif($|\?)|^data:image\/gif/i.test(p.src) ? opts.media.get(p.src) : undefined)) || opts.images?.get(p.src);
        ctx.save();
        ctx.beginPath();
        tracePath(ctx, outline);
        ctx.clip(rule);
        if (img) {
          const fl = p.filters;
          const css = imageFilterCss(fl);
          if (css && 'filter' in ctx) ctx.filter = css;
          // videos report their size as videoWidth/videoHeight
          const iw = (img as HTMLVideoElement).videoWidth || (img as HTMLImageElement).width || 1;
          const ih = (img as HTMLVideoElement).videoHeight || (img as HTMLImageElement).height || 1;
          ctx.globalAlpha *= p.opacity ?? 1;
          if (p.scaleMode === 'STRETCH') ctx.drawImage(img, 0, 0, n.width, n.height);
          else if (p.scaleMode === 'CROP' && p.crop) ctx.drawImage(img, p.crop.x, p.crop.y, iw * p.crop.scale, ih * p.crop.scale);
          else {
            const sc = p.scaleMode === 'FIT' ? Math.min(n.width / iw, n.height / ih) : Math.max(n.width / iw, n.height / ih);
            const w = iw * sc,
              h = ih * sc;
            ctx.drawImage(img, (n.width - w) / 2, (n.height - h) / 2, w, h);
          }
        } else {
          ctx.fillStyle = '#e5e5e5';
          ctx.fill();
          ctx.strokeStyle = '#c4c4c4';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(n.width, n.height);
          ctx.moveTo(n.width, 0);
          ctx.lineTo(0, n.height);
          ctx.stroke();
        }
        ctx.restore();
        continue;
      }
      if (p.type === 'GRADIENT_DIAMOND') {
        fillDiamond(ctx, p, outline, rule, n.width, n.height);
        continue;
      }
      const style = paintStyle(ctx, p, n.width, n.height);
      if (!style) continue;
      ctx.beginPath();
      tracePath(ctx, outline);
      ctx.fillStyle = style;
      ctx.fill(rule);
    }
  }

  private applyStrokeStyle(ctx: Ctx2D, n: NodeData, width: number) {
    ctx.lineWidth = width;
    ctx.lineJoin = n.strokeJoin === 'ROUND' ? 'round' : n.strokeJoin === 'BEVEL' ? 'bevel' : 'miter';
    ctx.lineCap = n.strokeCap === 'ROUND' ? 'round' : n.strokeCap === 'SQUARE' ? 'square' : 'butt';
    ctx.setLineDash(n.strokeDash ?? []);
  }

  private strokeOutline(ctx: Ctx2D, n: NodeData, outline: PathCmd[]) {
    if (sideWeights(n)) {
      // per-side weights: fill the ring between the outer and inner edges
      const ring = sideStrokeRing(n);
      for (const p of n.strokes) {
        if (p.visible === false) continue;
        const style = paintStyle(ctx, p, n.width, n.height);
        if (!style) continue;
        ctx.beginPath();
        tracePath(ctx, ring);
        ctx.fillStyle = style;
        ctx.fill('evenodd');
      }
      return;
    }
    if (!n.strokes.length || n.strokeWeight <= 0) return;
    if (hasDrawStroke(n)) return this.fillDrawStroke(ctx, n, outline);
    const open = n.type === 'VECTOR' && !outline.some((c) => c.c === 'Z');
    const align = open ? 'CENTER' : n.strokeAlign;
    // closed outlines lay their dashes out per segment, as Figma does (see dashOutline)
    const dashed = n.strokeDash?.length && !open ? dashOutline(outline, n.strokeDash) : null;
    const traceStroke = () => {
      if (!dashed) return tracePath(ctx, outline);
      for (const line of dashed) {
        ctx.moveTo(line[0].x, line[0].y);
        for (let i = 1; i < line.length; i++) ctx.lineTo(line[i].x, line[i].y);
      }
    };
    const dashStyle = (w: number) => {
      this.applyStrokeStyle(ctx, n, w);
      if (dashed) ctx.setLineDash([]);
    };
    for (const p of n.strokes) {
      if (p.visible === false) continue;
      const style = paintStyle(ctx, p, n.width, n.height);
      if (!style) continue;
      ctx.save();
      ctx.beginPath();
      tracePath(ctx, outline);
      if (align === 'INSIDE') {
        ctx.clip();
        ctx.beginPath();
        traceStroke();
        dashStyle(n.strokeWeight * 2);
      } else if (align === 'OUTSIDE') {
        const big = 1e6;
        ctx.save();
        ctx.beginPath();
        ctx.rect(-big, -big, big * 2, big * 2);
        tracePath(ctx, outline);
        ctx.clip('evenodd');
        ctx.beginPath();
        traceStroke();
        dashStyle(n.strokeWeight * 2);
        ctx.strokeStyle = style;
        ctx.stroke();
        ctx.restore();
        ctx.restore();
        continue;
      } else {
        ctx.beginPath();
        traceStroke();
        dashStyle(n.strokeWeight);
      }
      ctx.strokeStyle = style;
      ctx.stroke();
      ctx.restore();
    }
  }

  // ---------------------------------------------------------------- Figma Draw

  private postFor = new Set<NodeId>();
  private strokeCache = new Map<NodeId, { key: string; geo: DrawStroke }>();
  private tiles = new Map<string, { canvas: CanvasImageSource; w: number; h: number }>();
  private patternDepth = 0;

  /** Brushed, dynamic and variable-width strokes: filled outlines in the stroke paints. */
  private fillDrawStroke(ctx: Ctx2D, n: NodeData, outline: PathCmd[]) {
    const key = JSON.stringify([n.strokeWeight, n.strokeCap, n.strokeWidthProfile, n.strokeDynamic, n.strokeBrush, n.strokeBrush?.sourceId ? this.doc.version : 0, outline]);
    let hit = this.strokeCache.get(n.id);
    if (!hit || hit.key !== key) {
      hit = { key, geo: drawStrokeGeometry(n, outline, (id) => this.brushSource(id)) };
      if (this.strokeCache.size > 2000) this.strokeCache.clear();
      this.strokeCache.set(n.id, hit);
    }
    for (const p of n.strokes) {
      if (p.visible === false) continue;
      const style = paintStyle(ctx, p, n.width || 1, n.height || 1);
      if (!style) continue;
      ctx.beginPath();
      for (const poly of hit.geo.polygons) {
        if (!poly.length) continue;
        ctx.moveTo(poly[0].x, poly[0].y);
        for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
        ctx.closePath();
      }
      ctx.fillStyle = style;
      ctx.fill(hit.geo.rule);
    }
  }

  /** A custom brush's shape: the outlines of the source layer and its descendants, in its own space. */
  private brushSource(id: NodeId): Vec2[][] | null {
    const src = this.doc.get(id);
    if (!src) return null;
    const inv = invert(this.doc.worldTransform(id));
    const polys: Vec2[][] = [];
    this.doc.walk(id, (c) => {
      if (!c.visible || c.type === 'GROUP' || c.type === 'TEXT') return;
      const o = nodeOutline(c);
      if (!o) return;
      const t = mul(inv, this.doc.worldTransform(c.id));
      for (const line of flattenPath(o, 0.25)) polys.push(line.points.map((q) => ({ x: t[0] * q.x + t[2] * q.y + t[4], y: t[1] * q.x + t[3] * q.y + t[5] })));
    });
    return polys.length ? polys : null;
  }

  /** Pattern fill: the source layer rendered once as a tile, repeated across the shape. */
  private fillPattern(ctx: Ctx2D, n: NodeData, p: Extract<Paint, { type: 'PATTERN' }>, outline: PathCmd[], rule: CanvasFillRule, opts: RenderOptions, m: Mat) {
    const src = this.doc.get(p.sourceId);
    if (!src || src.id === n.id || this.patternDepth > 2) return;
    const sb = this.doc.worldBounds(src.id);
    const scale = p.scale ?? 1;
    const tw = sb.width * scale,
      th = sb.height * scale;
    if (tw < 0.5 || th < 0.5) return;
    const ds = Math.min(4, deviceScale(m) * scale) || 1;
    const key = `${src.id}|${ds.toFixed(2)}|${this.doc.version}`;
    let tile = this.tiles.get(key);
    if (!tile) {
      const w = Math.max(1, Math.ceil(sb.width * ds)),
        h = Math.max(1, Math.ceil(sb.height * ds));
      const c = (opts.createCanvas ?? defaultCanvas)(w, h);
      const tctx = c?.getContext('2d') as Ctx2D | null | undefined;
      if (!c || !tctx) return;
      this.patternDepth++;
      try {
        this.renderSubtree(tctx, src.id, [ds, 0, 0, ds, -sb.x * ds, -sb.y * ds], { ...opts, viewport: undefined, background: false });
      } finally {
        this.patternDepth--;
      }
      if (this.tiles.size > 64) this.tiles.clear();
      this.tiles.set(key, (tile = { canvas: c as unknown as CanvasImageSource, w, h }));
    }
    const gx = tw + (p.spacing?.x ?? 0),
      gy = th + (p.spacing?.y ?? 0);
    ctx.save();
    ctx.beginPath();
    tracePath(ctx, outline);
    ctx.clip(rule);
    ctx.globalAlpha *= p.opacity ?? 1;
    const rows = Math.ceil(n.height / gy) + 1,
      cols = Math.ceil(n.width / gx) + 1;
    for (let r = -1; r < rows; r++)
      for (let c = -1; c < cols; c++) {
        const x = c * gx + (p.tile === 'HORIZONTAL_HEXAGONAL' && r & 1 ? gx / 2 : 0);
        const y = r * gy + (p.tile === 'VERTICAL_HEXAGONAL' && c & 1 ? gy / 2 : 0);
        ctx.drawImage(tile.canvas, x, y, tw, th);
      }
    ctx.restore();
  }

  /**
   * Glass: the backdrop magnified (refraction) and blurred inside the shape, its color channels
   * scaled apart (dispersion), and a highlight along the edge facing the light.
   */
  private drawGlass(ctx: Ctx2D, n: NodeData, m: Mat, outline: PathCmd[], opts: RenderOptions) {
    const e = n.effects.find((x): x is Extract<Effect, { type: 'GLASS' }> => x.type === 'GLASS' && x.visible !== false);
    const canvas = ctx.canvas as unknown as CanvasImageSource & { width: number; height: number };
    if (!e || !canvas?.width || !outline.length) return;
    if (opts.isolated) {
      this.backdropMask(ctx, n, outline);
      this.glassEdge(ctx, n, outline, e);
      return;
    }
    const W = canvas.width,
      H = canvas.height;
    const mk = (w: number, h: number) => {
      const c = (opts.createCanvas ?? defaultCanvas)(w, h);
      return { c: c as unknown as CanvasImageSource, x: c?.getContext('2d') as Ctx2D | null | undefined };
    };
    const back = mk(W, H);
    if (!back.x) return;
    back.x.drawImage(canvas, 0, 0);
    const s = deviceScale(m);
    const center = { x: m[0] * (n.width / 2) + m[2] * (n.height / 2) + m[4], y: m[1] * (n.width / 2) + m[3] * (n.height / 2) + m[5] };
    const k = 1 + (e.refraction ?? 0.5) * 0.18;
    const disp = (e.dispersion ?? 0) * 0.04;
    // the backdrop, magnified about the shape's center; with dispersion each channel a little differently
    const lens = mk(W, H);
    if (!lens.x) return;
    const drawScaled = (target: Ctx2D, scale: number) => {
      target.save();
      target.setTransform(scale, 0, 0, scale, center.x * (1 - scale), center.y * (1 - scale));
      if ('filter' in target && (e.radius ?? 0) > 0) target.filter = `blur(${((e.radius ?? 0) * s) / 2 / scale}px)`;
      target.drawImage(back.c, 0, 0);
      target.restore();
    };
    if (disp > 0) {
      for (const [color, scale] of [['#ff0000', k * (1 + disp)], ['#00ff00', k], ['#0000ff', k * (1 - disp)]] as const) {
        const ch = mk(W, H);
        if (!ch.x) return;
        drawScaled(ch.x, scale);
        ch.x.globalCompositeOperation = 'multiply';
        ch.x.fillStyle = color;
        ch.x.fillRect(0, 0, W, H);
        ch.x.globalCompositeOperation = 'destination-in';
        drawScaled(ch.x, scale); // keep the backdrop's alpha
        lens.x.globalCompositeOperation = 'lighter';
        lens.x.drawImage(ch.c, 0, 0);
      }
    } else drawScaled(lens.x, k);
    ctx.save();
    ctx.beginPath();
    tracePath(ctx, outline);
    ctx.clip(n.windingRule === 'EVENODD' ? 'evenodd' : 'nonzero');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(lens.c, 0, 0);
    ctx.restore();
    ctx.restore();
    this.glassEdge(ctx, n, outline, e);
  }

  /** A shape the GPU compositor can find (alpha 2/255), for backdrop effects on isolated rasters. */
  private backdropMask(ctx: Ctx2D, n: NodeData, outline: PathCmd[]) {
    ctx.save();
    ctx.beginPath();
    tracePath(ctx, outline);
    ctx.fillStyle = 'rgba(255,255,255,0.008)';
    ctx.fill(n.windingRule === 'EVENODD' ? 'evenodd' : 'nonzero');
    ctx.restore();
  }

  /** Glass: the lit edge, a band along the inside of the outline, bright where it faces the light. */
  private glassEdge(ctx: Ctx2D, n: NodeData, outline: PathCmd[], e: Extract<Effect, { type: 'GLASS' }>) {
    ctx.save();
    ctx.beginPath();
    tracePath(ctx, outline);
    ctx.clip(n.windingRule === 'EVENODD' ? 'evenodd' : 'nonzero');
    const ang = ((e.lightAngle ?? -45) * Math.PI) / 180;
    const cx = n.width / 2,
      cy = n.height / 2,
      r = Math.hypot(cx, cy);
    const g = ctx.createLinearGradient(cx + Math.cos(ang) * r, cy + Math.sin(ang) * r, cx - Math.cos(ang) * r, cy - Math.sin(ang) * r);
    const li = e.lightIntensity ?? 0.5;
    g.addColorStop(0, `rgba(255,255,255,${li})`);
    g.addColorStop(0.45, 'rgba(255,255,255,0)');
    g.addColorStop(0.75, 'rgba(255,255,255,0)');
    g.addColorStop(1, `rgba(255,255,255,${li * 0.35})`);
    ctx.beginPath();
    tracePath(ctx, outline);
    ctx.lineWidth = Math.max(1, e.depth ?? 4) * 2;
    ctx.strokeStyle = g;
    ctx.stroke();
    ctx.restore();
  }

  /** Inner shadows: the shadow of a frame around the shape, kept inside it. */
  private drawInnerShadows(ctx: Ctx2D, n: NodeData, m: Mat, outline: PathCmd[]) {
    const list = n.effects.filter((e): e is Extract<Effect, { type: 'DROP_SHADOW' | 'INNER_SHADOW' }> => e.type === 'INNER_SHADOW' && e.visible !== false);
    if (!list.length || !outline.length) return;
    const s = deviceScale(m);
    const rule = n.windingRule === 'EVENODD' ? 'evenodd' : 'nonzero';
    for (const e of list) {
      ctx.save();
      ctx.beginPath();
      tracePath(ctx, outline);
      ctx.clip(rule);
      const big = Math.max(n.width, n.height) + e.radius * 4 + Math.abs(e.offset.x) + Math.abs(e.offset.y) + (e.spread ?? 0) * 2 + 10;
      ctx.shadowColor = colorToCss(e.color);
      ctx.shadowBlur = e.radius * s;
      ctx.shadowOffsetX = e.offset.x * s;
      ctx.shadowOffsetY = e.offset.y * s;
      ctx.beginPath();
      ctx.rect(-big, -big, n.width + big * 2, n.height + big * 2);
      tracePath(ctx, outline);
      ctx.fillStyle = colorToCss({ ...e.color, a: 1 });
      ctx.fill('evenodd');
      if (e.spread && e.spread > 0) {
        // spread: a band along the inside of the edge
        ctx.beginPath();
        tracePath(ctx, outline);
        ctx.lineWidth = e.spread * 2;
        ctx.strokeStyle = colorToCss(e.color);
        ctx.stroke();
      }
      ctx.restore();
    }
  }

  /** Background blur: what's already drawn behind the layer, blurred inside its shape. */
  private drawBackdrop(ctx: Ctx2D, n: NodeData, m: Mat, outline: PathCmd[], opts: RenderOptions) {
    const e = n.effects.find((x): x is Extract<Effect, { type: 'LAYER_BLUR' | 'BACKGROUND_BLUR' }> => x.type === 'BACKGROUND_BLUR' && x.visible !== false);
    const canvas = ctx.canvas as unknown as CanvasImageSource & { width: number; height: number };
    if (!e || !canvas?.width || !outline.length || !('filter' in ctx)) return;
    if (opts.isolated) return this.backdropMask(ctx, n, outline);
    const W = canvas.width,
      H = canvas.height;
    const copy = (opts.createCanvas ?? defaultCanvas)(W, H);
    const cctx = copy?.getContext('2d') as Ctx2D | null | undefined;
    if (!copy || !cctx) return;
    cctx.drawImage(canvas, 0, 0);
    const s = deviceScale(m);
    ctx.save();
    ctx.beginPath();
    tracePath(ctx, outline);
    ctx.clip(n.windingRule === 'EVENODD' ? 'evenodd' : 'nonzero');
    const draw = (target: Ctx2D, radius: number) => {
      target.save();
      target.setTransform(1, 0, 0, 1, 0, 0);
      target.filter = radius > 0 ? `blur(${(radius * s) / 2}px)` : 'none';
      target.drawImage(copy as unknown as CanvasImageSource, 0, 0);
      target.restore();
    };
    if (e.blurType === 'PROGRESSIVE') this.progressive(ctx, n, m, e, W, H, opts, (t, r) => draw(t, r));
    else draw(ctx, e.radius);
    ctx.restore();
  }

  /**
   * Progressive blur: K strengths from startRadius to radius, each kept where the ramp is near its
   * share (triangle weights that sum to one), so strengths blend smoothly along the ramp.
   */
  private progressive(ctx: Ctx2D, n: NodeData, m: Mat, e: { radius: number; startRadius?: number; start?: Vec2; end?: Vec2 }, W: number, H: number, opts: RenderOptions, draw: (target: Ctx2D, radius: number) => void) {
    const K = 8;
    // the weighted strengths are added up offscreen (they sum to one), then drawn once
    const acc = (opts.createCanvas ?? defaultCanvas)(W, H);
    const actx = acc?.getContext('2d') as Ctx2D | null | undefined;
    if (!acc || !actx) return;
    const a = e.start ?? { x: 0.5, y: 0 },
      b = e.end ?? { x: 0.5, y: 1 };
    const pa = { x: m[0] * a.x * n.width + m[2] * a.y * n.height + m[4], y: m[1] * a.x * n.width + m[3] * a.y * n.height + m[5] };
    const pb = { x: m[0] * b.x * n.width + m[2] * b.y * n.height + m[4], y: m[1] * b.x * n.width + m[3] * b.y * n.height + m[5] };
    for (let k = 0; k < K; k++) {
      const band = (opts.createCanvas ?? defaultCanvas)(W, H);
      const bctx = band?.getContext('2d') as Ctx2D | null | undefined;
      if (!band || !bctx) return;
      draw(bctx, (e.startRadius ?? 0) + ((e.radius - (e.startRadius ?? 0)) * k) / (K - 1));
      const g = bctx.createLinearGradient(pa.x, pa.y, pb.x, pb.y);
      const t = k / (K - 1),
        d = 1 / (K - 1);
      g.addColorStop(0, `rgba(0,0,0,${k === 0 ? 1 : 0})`);
      if (k > 0) g.addColorStop(Math.max(0, t - d), 'rgba(0,0,0,0)');
      g.addColorStop(t, 'rgba(0,0,0,1)');
      if (k < K - 1) g.addColorStop(Math.min(1, t + d), 'rgba(0,0,0,0)');
      g.addColorStop(1, `rgba(0,0,0,${k === K - 1 ? 1 : 0})`);
      bctx.save();
      bctx.setTransform(1, 0, 0, 1, 0, 0);
      bctx.globalCompositeOperation = 'destination-in';
      bctx.fillStyle = g;
      bctx.fillRect(0, 0, W, H);
      bctx.restore();
      actx.globalCompositeOperation = 'lighter';
      actx.drawImage(band as unknown as CanvasImageSource, 0, 0);
    }
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(acc as unknown as CanvasImageSource, 0, 0);
    ctx.restore();
  }

  /** Texture and progressive layer blur: the layer drawn offscreen, processed, then composited. */
  private postProcess(ctx: Ctx2D, n: NodeData, parentM: Mat, opts: RenderOptions, alpha: number, post: Effect[]): boolean {
    const canvas = ctx.canvas as unknown as { width: number; height: number };
    const W = canvas.width,
      H = canvas.height;
    const layer = (opts.createCanvas ?? defaultCanvas)(W, H);
    const lctx = layer?.getContext('2d') as Ctx2D | null | undefined;
    if (!layer || !lctx) return false;
    const cur = ctx.getTransform?.();
    if (cur) lctx.setTransform(cur);
    this.postFor.add(n.id);
    try {
      this.renderNode(lctx, n, parentM, opts, alpha);
    } finally {
      this.postFor.delete(n.id);
    }
    const m = mul(parentM, localTransform(n));
    let src: AtlasImage = layer as unknown as AtlasImage;
    const tex = post.find((e): e is Extract<Effect, { type: 'TEXTURE' }> => e.type === 'TEXTURE');
    if (tex) src = this.texture(src, n, m, tex, W, H, opts) ?? src;
    const shaders = post.filter((e): e is Extract<Effect, { type: 'SHADER' }> => e.type === 'SHADER');
    if (shaders.length) src = this.shaderEffects(src, n, m, shaders, W, H, opts);
    const pblur = post.find((e): e is Extract<Effect, { type: 'LAYER_BLUR' | 'BACKGROUND_BLUR' }> => e.type === 'LAYER_BLUR');
    ctx.save();
    if (tex?.clipToShape) {
      setTransform(ctx, m);
      ctx.beginPath();
      tracePath(ctx, nodeOutline(n) ?? []);
      ctx.clip();
    }
    if (pblur && 'filter' in ctx) {
      const s = deviceScale(m);
      this.progressive(ctx, n, m, pblur, W, H, opts, (t, r) => {
        t.save();
        t.setTransform(1, 0, 0, 1, 0, 0);
        t.filter = r > 0 ? `blur(${(r * s) / 2}px)` : 'none';
        t.drawImage(src, 0, 0);
        t.restore();
      });
    } else {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(src, 0, 0);
    }
    ctx.restore();
    return true;
  }

  /** Shader fill: the shader's pixels at the layer's device resolution, clipped to its shape. */
  private fillShader(ctx: Ctx2D, n: NodeData, p: Extract<Paint, { type: 'SHADER' }>, index: number, outline: PathCmd[], rule: CanvasFillRule, opts: RenderOptions, m: Mat) {
    if (n.width <= 0 || n.height <= 0) return;
    const s = Math.min(deviceScale(m), 4096 / Math.max(n.width, n.height));
    const width = Math.max(1, Math.round(n.width * s)),
      height = Math.max(1, Math.round(n.height * s));
    const img = opts.shaders?.get({ kind: 'fill', nodeId: n.id, index, source: p.source, params: p.params, time: p.time, name: p.name, width, height });
    ctx.save();
    ctx.beginPath();
    tracePath(ctx, outline);
    ctx.clip(rule);
    ctx.globalAlpha *= p.opacity ?? 1;
    if (img) ctx.drawImage(img, 0, 0, n.width, n.height);
    else {
      // not rendered yet (or no GPU here): a quiet stand-in
      const g = ctx.createLinearGradient(0, 0, n.width, n.height);
      g.addColorStop(0, '#e9e6f5');
      g.addColorStop(1, '#d5e4f2');
      ctx.fillStyle = g;
      ctx.fill(rule);
    }
    ctx.restore();
  }

  /** Shader effects: hand the layer's pixels (its device-space box) to the shader and draw what comes back. */
  private shaderEffects(src: AtlasImage, n: NodeData, m: Mat, list: Extract<Effect, { type: 'SHADER' }>[], W: number, H: number, opts: RenderOptions): AtlasImage {
    if (!opts.shaders) return src;
    const b = transformRect(m, { x: 0, y: 0, width: n.width, height: n.height });
    const x0 = Math.max(0, Math.floor(b.x)),
      y0 = Math.max(0, Math.floor(b.y));
    const w = Math.min(W, Math.ceil(b.x + b.width)) - x0,
      h = Math.min(H, Math.ceil(b.y + b.height)) - y0;
    if (w <= 0 || h <= 0 || w * h > 16e6) return src;
    let cur = src;
    for (const e of list) {
      const input = (opts.createCanvas ?? defaultCanvas)(w, h);
      const ictx = input?.getContext('2d') as Ctx2D | null | undefined;
      if (!input || !ictx) return cur;
      ictx.drawImage(cur, -x0, -y0);
      const img = opts.shaders.get({ kind: 'effect', nodeId: n.id, index: n.effects.indexOf(e), source: e.source, params: e.params, time: e.time, name: e.name, width: w, height: h, input: input as unknown as ShaderRequest['input'] & object });
      if (!img) continue;
      const out = (opts.createCanvas ?? defaultCanvas)(W, H);
      const octx = out?.getContext('2d') as Ctx2D | null | undefined;
      if (!out || !octx) return cur;
      // the shader owns the layer's box; what the layer drew outside it (shadows, strokes) stays
      octx.drawImage(cur, 0, 0);
      octx.clearRect(x0, y0, w, h);
      octx.drawImage(img, x0, y0, w, h);
      cur = out as unknown as AtlasImage;
    }
    return cur;
  }

  /** Displace the layer's pixels by smooth noise (rough, frosted edges). */
  private texture(src: AtlasImage, n: NodeData, m: Mat, e: Extract<Effect, { type: 'TEXTURE' }>, W: number, H: number, opts: RenderOptions): AtlasImage | null {
    const s = deviceScale(m);
    const reach = (e.radius ?? 4) * s * 2;
    const b = transformRect(m, { x: 0, y: 0, width: n.width, height: n.height });
    const x0 = Math.max(0, Math.floor(b.x - reach)),
      y0 = Math.max(0, Math.floor(b.y - reach));
    const x1 = Math.min(W, Math.ceil(b.x + b.width + reach)),
      y1 = Math.min(H, Math.ceil(b.y + b.height + reach));
    const w = x1 - x0,
      h = y1 - y0;
    if (w <= 0 || h <= 0 || w * h > 16e6) return null;
    const read = (opts.createCanvas ?? defaultCanvas)(W, H);
    const rctx = read?.getContext('2d') as Ctx2D | null | undefined;
    if (!read || !rctx) return null;
    rctx.drawImage(src, 0, 0);
    const input = rctx.getImageData(x0, y0, w, h);
    const out = rctx.createImageData(w, h);
    const size = Math.max(0.5, (e.size ?? 4) * s);
    const amp = (e.radius ?? 4) * s;
    const seed = e.seed ?? seedOf(n.id, 7);
    const iD = input.data,
      oD = out.data;
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const gx = (x0 + x) / size,
          gy = (y0 + y) / size;
        const dx = Math.round(noise2(gx, gy, seed) * amp),
          dy = Math.round(noise2(gx + 31.7, gy - 17.3, seed + 1) * amp);
        const sx = x + dx,
          sy = y + dy;
        if (sx < 0 || sy < 0 || sx >= w || sy >= h) continue;
        const i = (y * w + x) * 4,
          j = (sy * w + sx) * 4;
        oD[i] = iD[j];
        oD[i + 1] = iD[j + 1];
        oD[i + 2] = iD[j + 2];
        oD[i + 3] = iD[j + 3];
      }
    rctx.clearRect(0, 0, W, H);
    rctx.putImageData(out, x0, y0);
    return read as unknown as AtlasImage;
  }

  /** Noise: grains drawn over the layer, inside its shape. */
  private drawNoise(ctx: Ctx2D, n: NodeData, m: Mat, outline: PathCmd[], opts: RenderOptions) {
    const list = n.effects.filter((e): e is Extract<Effect, { type: 'NOISE' }> => e.type === 'NOISE' && e.visible !== false);
    if (!list.length || !outline.length) return;
    for (const e of list) {
      const tile = this.noiseTile(e, n, opts);
      if (!tile) continue;
      const g = Math.max(0.25, e.size ?? 1);
      const span = 64 * g;
      ctx.save();
      ctx.beginPath();
      tracePath(ctx, outline);
      ctx.clip(n.windingRule === 'EVENODD' ? 'evenodd' : 'nonzero');
      ctx.imageSmoothingEnabled = false;
      for (let y = 0; y < n.height; y += span) for (let x = 0; x < n.width; x += span) ctx.drawImage(tile, x, y, span, span);
      ctx.restore();
    }
    void m;
  }

  private noiseTile(e: Extract<Effect, { type: 'NOISE' }>, n: NodeData, opts: RenderOptions): CanvasImageSource | null {
    const key = `noise|${JSON.stringify([e.noiseType, e.color, e.secondaryColor, e.density, e.opacity, e.seed ?? seedOf(n.id, 3)])}`;
    const hit = this.tiles.get(key);
    if (hit) return hit.canvas;
    const c = (opts.createCanvas ?? defaultCanvas)(64, 64);
    const tctx = c?.getContext('2d') as Ctx2D | null | undefined;
    if (!c || !tctx) return null;
    const img = tctx.createImageData(64, 64);
    const seed = e.seed ?? seedOf(n.id, 3);
    const density = e.density ?? 0.5,
      op = e.opacity ?? 0.25;
    const c1: Color = e.color ?? { r: 0, g: 0, b: 0, a: 1 },
      c2: Color = e.secondaryColor ?? { r: 1, g: 1, b: 1, a: 1 };
    for (let i = 0; i < 64 * 64; i++) {
      const r = hash2(i, seed);
      if (r >= density) continue;
      let col = c1;
      if (e.noiseType === 'DUOTONE') col = hash2(i, seed + 9) < 0.5 ? c1 : c2;
      else if (e.noiseType === 'MULTITONE') col = hsl(hash2(i, seed + 5), 0.8, 0.55);
      img.data[i * 4] = Math.round(col.r * 255);
      img.data[i * 4 + 1] = Math.round(col.g * 255);
      img.data[i * 4 + 2] = Math.round(col.b * 255);
      img.data[i * 4 + 3] = Math.round((col.a ?? 1) * op * 255);
    }
    tctx.putImageData(img, 0, 0);
    if (this.tiles.size > 64) this.tiles.clear();
    this.tiles.set(key, { canvas: c as unknown as CanvasImageSource, w: 64, h: 64 });
    return c as unknown as CanvasImageSource;
  }

  private drawLine(ctx: Ctx2D, n: NodeData) {
    const seg = lineSegment(n);
    if (hasDrawStroke(n)) {
      this.fillDrawStroke(ctx, n, [
        { c: 'M', x: seg.a.x, y: seg.a.y },
        { c: 'L', x: seg.b.x, y: seg.b.y },
      ]);
      return;
    }
    for (const p of n.strokes) {
      if (p.visible === false) continue;
      const style = paintStyle(ctx, p, n.width, Math.max(1, n.height));
      if (!style) continue;
      this.applyStrokeStyle(ctx, n, n.strokeWeight);
      ctx.strokeStyle = style;
      ctx.fillStyle = style;
      const { a, b } = seg;
      const trimA = capInset(n.startCap, n.strokeWeight);
      const trimB = capInset(n.endCap, n.strokeWeight);
      ctx.beginPath();
      ctx.moveTo(a.x + trimA, a.y);
      ctx.lineTo(b.x - trimB, b.y);
      ctx.stroke();
      drawCap(ctx, n.startCap, a, { x: -1, y: 0 }, n.strokeWeight);
      drawCap(ctx, n.endCap, b, { x: 1, y: 0 }, n.strokeWeight);
    }
  }

  private drawConnector(ctx: Ctx2D, n: NodeData, parentM: Mat) {
    const g = connectorGeometry(this.doc, n);
    // Connector geometry is in page space; parentM maps the connector's parent to device.
    const pageToDevice = mul(parentM, invertLinear(this.doc, n));
    setTransform(ctx, pageToDevice);
    const stroke = n.strokes.find((p) => p.visible !== false);
    const style = stroke ? paintStyle(ctx, stroke, 1, 1) : '#1e1e1e';
    ctx.strokeStyle = style ?? '#1e1e1e';
    ctx.fillStyle = style ?? '#1e1e1e';
    this.applyStrokeStyle(ctx, n, n.strokeWeight);
    ctx.lineJoin = 'round';
    const pts = g.points;
    const p0 = pts[0],
      p1 = pts[pts.length - 1];
    let d0: Vec2, d1: Vec2;
    if (g.kind === 'CURVED') {
      d0 = norm({ x: p0.x - pts[1].x, y: p0.y - pts[1].y });
      d1 = norm({ x: p1.x - pts[2].x, y: p1.y - pts[2].y });
    } else {
      d0 = norm({ x: p0.x - pts[1].x, y: p0.y - pts[1].y });
      d1 = norm({ x: p1.x - pts[pts.length - 2].x, y: p1.y - pts[pts.length - 2].y });
    }
    const t0 = capInset(n.startCap, n.strokeWeight);
    const t1 = capInset(n.endCap, n.strokeWeight);
    ctx.beginPath();
    if (g.kind === 'CURVED') {
      ctx.moveTo(p0.x - d0.x * t0, p0.y - d0.y * t0);
      ctx.bezierCurveTo(pts[1].x, pts[1].y, pts[2].x, pts[2].y, p1.x - d1.x * t1, p1.y - d1.y * t1);
    } else {
      ctx.moveTo(p0.x - d0.x * t0, p0.y - d0.y * t0);
      for (let i = 1; i < pts.length - 1; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.lineTo(p1.x - d1.x * t1, p1.y - d1.y * t1);
    }
    ctx.stroke();
    ctx.setLineDash([]);
    drawCap(ctx, n.startCap, p0, d0, n.strokeWeight);
    drawCap(ctx, n.endCap, p1, d1, n.strokeWeight);
    if (n.characters) {
      const mid = connectorMidpoint(g.kind, pts);
      const layout = layoutNodeText({ ...n, type: 'TEXT', textAutoResize: 'WIDTH_AND_HEIGHT' });
      ctx.save();
      ctx.translate(mid.x - layout.width / 2, mid.y - layout.height / 2);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(-6, -2, layout.width + 12, layout.height + 4);
      this.drawText(ctx, n, layout, n.textColor ? [{ type: 'SOLID', color: n.textColor }] : []);
      ctx.restore();
    }
  }

  private drawMarkers(ctx: Ctx2D, layout: TextLayout) {
    ctx.font = layout.font;
    const fs = layout.style.fontSize;
    for (const line of layout.lines) {
      if (!line.marker) continue;
      const b = bulletShape(line.marker, line.markerX ?? 0, line.baseline, fs);
      if (!b) {
        drawRun(ctx, line.marker, line.markerX ?? 0, line.baseline, 0, layout.font, layout.style.features);
        continue;
      }
      ctx.beginPath();
      if (b.kind === 'square') ctx.rect(b.cx - b.r, b.cy - b.r, b.r * 2, b.r * 2);
      else ctx.arc(b.cx, b.cy, b.kind === 'circle' ? b.r * 0.85 : b.r, 0, Math.PI * 2);
      if (b.kind === 'circle') {
        ctx.lineWidth = Math.max(0.75, fs * 0.06);
        ctx.strokeStyle = ctx.fillStyle;
        ctx.stroke();
      } else ctx.fill();
    }
  }

  private drawRich(ctx: Ctx2D, layout: TextLayout, baseStyle: string | CanvasGradient) {
    const styles = layout.styles!;
    ctx.textBaseline = 'alphabetic';
    this.drawMarkers(ctx, layout);
    for (const line of layout.lines) {
      for (const p of line.pieces ?? []) {
        const st = styles[p.style];
        ctx.font = st.font;
        ctx.fillStyle = st.color ? colorToCss(st.color) : st.link ? '#0d99ff' : baseStyle;
        const x = line.x + p.x;
        drawRun(ctx, p.text, x, line.baseline, st.letterSpacing, st.font, st.features);
        if (st.decoration !== 'NONE') {
          const y = st.decoration === 'UNDERLINE' ? line.baseline + st.fontSize * UNDERLINE_OFFSET : line.baseline - st.fontSize * 0.3;
          ctx.fillRect(x, y, p.width, Math.max(1, st.fontSize * UNDERLINE_THICKNESS));
        }
      }
    }
  }

  private drawTextOnPath(ctx: Ctx2D, n: NodeData, fill: Paint) {
    const l = layoutTextOnPath(n);
    const base = paintStyle(ctx, fill, n.width || 1, n.height || 1) ?? '#000';
    ctx.textBaseline = 'alphabetic';
    for (const g of l.glyphs) {
      const st = l.styles[g.style];
      ctx.save();
      ctx.translate(g.x, g.y);
      ctx.rotate(g.angle);
      ctx.font = st.font;
      ctx.fillStyle = st.color ? colorToCss(st.color) : st.link ? '#0d99ff' : base;
      drawRun(ctx, g.text, 0, 0, 0, st.font, st.features);
      if (st.decoration !== 'NONE') ctx.fillRect(0, st.decoration === 'UNDERLINE' ? st.fontSize * UNDERLINE_OFFSET : -st.fontSize * 0.3, g.width, Math.max(1, st.fontSize * UNDERLINE_THICKNESS));
      ctx.restore();
    }
  }

  drawText(ctx: Ctx2D, n: NodeData, layout: TextLayout, fills: Paint[]) {
    const fill = fills.find((p) => p.visible !== false);
    if (!fill) return;
    if (n.textPath && n.type === 'TEXT') return this.drawTextOnPath(ctx, n, fill);
    const style = paintStyle(ctx, fill, layout.width || 1, layout.height || 1) ?? '#000';
    ctx.fillStyle = style;
    ctx.font = layout.font;
    ctx.textBaseline = 'alphabetic';
    const ls = layout.style.letterSpacing;
    if (layout.styles) return this.drawRich(ctx, layout, style);
    this.drawMarkers(ctx, layout);
    for (const line of layout.lines) {
      if (!line.text) continue;
      // bidirectional lines come as direction runs in visual order
      if (line.pieces) for (const p of line.pieces) drawRun(ctx, p.text, line.x + p.x, line.baseline, ls, layout.font, layout.style.features);
      else drawRun(ctx, line.text, line.x, line.baseline, ls, layout.font, layout.style.features);
      if (layout.style.decoration !== 'NONE') {
        const y = layout.style.decoration === 'UNDERLINE' ? line.baseline + layout.style.fontSize * UNDERLINE_OFFSET : line.baseline - layout.style.fontSize * 0.3;
        ctx.fillRect(line.x, y, line.width, Math.max(1, layout.style.fontSize * UNDERLINE_THICKNESS));
      }
    }
    void n;
  }
}

/** One line of text in a cssFont() font, shaped like design text (labels drawn by renderers). */
export function fillTextRun(ctx: Ctx2D, text: string, x: number, y: number, font: string, letterSpacing = 0, features?: GlyphFeatures) {
  ctx.font = font;
  drawRun(ctx, text, x, y, letterSpacing, font, features);
}

/** Draw a run of text the way the layout measured it: shaped glyph outlines when the shaper has the font. */
function drawRun(ctx: Ctx2D, text: string, x: number, y: number, ls: number, font: string, fx?: GlyphFeatures) {
  const sh = shapeRun(text, font, fx);
  if (!sh) return drawRunWithCanvas(ctx, text, x, y, ls, font, fx);
  const shaper = getTextShaper()!;
  const cps = ls ? codePointsBefore(text, sh.rtl) : null;
  if (!drawFromAtlas(ctx, text, sh, shaper, x, y, ls, cps)) {
    const path = PathImpl ? runPath(sh, shaper, x, y, ls, cps) : null;
    if (path) ctx.fill(path);
    else traceGlyphs(ctx, sh, shaper, x, y, ls, cps);
  }
  // characters no loaded font has (emoji, other scripts): the canvas's own text engine
  const ff = withFallbacks(font);
  for (const f of sh.fallback) {
    ctx.font = ff;
    drawRunWithCanvas(ctx, text.slice(f.start, f.end), x + f.x + (cps ? ls * cps[f.start] : 0), y, ls, ff, fx);
  }
  if (sh.fallback.length) ctx.font = font;
}

type PathCtor = new (d?: string) => Path2D;
let PathImpl: PathCtor | null = typeof Path2D !== 'undefined' ? Path2D : null;
/** Hosts without a global Path2D (Node canvases) pass theirs, so glyph outlines are built once and reused. */
let conicFixedStart = false;
/**
 * Canvas conic gradients start at the given angle (the spec; 0 = 3 o'clock, as in Figma). Skia's
 * (headless) ignore it and always start at 12 o'clock; headless renderers set this so the stops
 * are turned to match.
 */
export function setConicFixedStart(on: boolean) {
  conicFixedStart = on;
}

/** Stops of a sweep shifted by `off` (0..1) around the circle, split where they wrap. */
function rotateStops(stops: { position: number; color: Color }[], off: number): { position: number; color: Color }[] {
  if (!stops.length || off === 0) return stops;
  const sorted = [...stops].sort((a, b) => a.position - b.position);
  // the colour at the seam (sweep position 1 - off), interpolated around the circle
  const at = (t: number): Color => {
    const first = sorted[0],
      last = sorted[sorted.length - 1];
    if (t <= first.position || t >= last.position) {
      // between the last stop and the first, across 1 → 0
      const span = 1 - last.position + first.position;
      const k = span > 0 ? ((t - last.position + 1) % 1) / span : 0;
      return mixColor(last.color, first.color, k);
    }
    for (let i = 1; i < sorted.length; i++)
      if (t <= sorted[i].position) {
        const a = sorted[i - 1],
          b = sorted[i];
        return mixColor(a.color, b.color, b.position > a.position ? (t - a.position) / (b.position - a.position) : 0);
      }
    return last.color;
  };
  const seam = at(1 - off);
  // stops pushed past 1 wrap to the start; where one lands on an unwrapped stop, it comes first
  // (the sweep reaches the end of the gradient just before it starts over)
  const out = sorted.map((s) => {
    const q = s.position + off;
    return { position: q >= 1 ? q - 1 : q, color: s.color, wrapped: q >= 1 ? 0 : 1 };
  });
  out.push({ position: 0, color: seam, wrapped: -1 }, { position: 1, color: seam, wrapped: 2 });
  return out.sort((a, b) => a.position - b.position || a.wrapped - b.wrapped).map(({ position, color }) => ({ position, color }));
}

function mixColor(a: Color, b: Color, k: number): Color {
  return { r: a.r + (b.r - a.r) * k, g: a.g + (b.g - a.g) * k, b: a.b + (b.b - a.b) * k, a: a.a + (b.a - a.a) * k };
}

export function setPath2D(ctor: PathCtor | null) {
  PathImpl = ctor;
}

// glyph outlines as paths (em units), and shaped lines assembled from them, reused across draws
const glyphPaths = new WeakMap<Float32Array, Path2D>();
const runPaths = new WeakMap<ShapedText, Map<string, Path2D>>();

function glyphPath(o: Float32Array): Path2D {
  let p = glyphPaths.get(o);
  if (!p) {
    let d = '';
    for (let i = 0; i < o.length; ) {
      const op = o[i];
      const n = op === 2 ? 4 : op === 3 ? 6 : op === 4 ? 0 : 2;
      d += 'MLQCZ'[op];
      for (let j = 1; j <= n; j++) d += (j > 1 ? ' ' : '') + Math.round(o[i + j] * 1e5) / 1e5;
      i += n + 1;
    }
    p = new PathImpl!(d);
    glyphPaths.set(o, p);
  }
  return p;
}

function runPath(sh: ShapedText, shaper: TextShaper, x: number, y: number, ls: number, cps: Uint32Array | null): Path2D | null {
  let byPos = runPaths.get(sh);
  if (!byPos) runPaths.set(sh, (byPos = new Map()));
  const key = `${x}|${y}|${ls}`;
  let p = byPos.get(key);
  if (!p) {
    p = new PathImpl!();
    if (typeof p.addPath !== 'function') return null;
    for (const g of sh.glyphs) {
      const o = shaper.outline(g.face, g.gid);
      if (!o) continue;
      const k = g.size;
      // scale to the font size, slant for synthetic italic (x' = x - skew·y), then place
      p.addPath(glyphPath(o), { a: k, b: 0, c: -g.skew * k, d: k, e: x + g.x + (cps ? ls * cps[g.cluster] : 0), f: y + g.y });
    }
    if (byPos.size > 16) byPos.clear();
    byPos.set(key, p);
  }
  return p;
}

// ---------------------------------------------------------------- glyph atlas
// Filling outlines rasterizes every glyph on every draw; browsers' own fillText avoids that with a
// glyph cache, and so does this: each glyph is rendered once per color, device size and quarter-
// pixel offset into an atlas page, then copied. Only where that's exact: solid fills, no rotation
// or skew in the transform, glyphs up to 96 device px. Everything else fills outlines.

type AtlasCanvas = CanvasImageSource & { getContext(id: '2d', opts?: { willReadFrequently?: boolean }): unknown; width: number; height: number };
let makeCanvas: ((w: number, h: number) => AtlasCanvas) | null = typeof OffscreenCanvas !== 'undefined' ? (w, h) => new OffscreenCanvas(w, h) as unknown as AtlasCanvas : null;
let atlasOn = true;
/** Hosts without OffscreenCanvas (Node) pass a canvas factory for the glyph atlas. */
export function setGlyphCanvasFactory(f: ((w: number, h: number) => AtlasCanvas) | null) {
  makeCanvas = f;
  resetAtlas();
}
/** Vector output (PDF) turns the atlas off so text stays outlines. */
export function setGlyphAtlas(on: boolean) {
  atlasOn = on;
}

const PAGE = 1024;
const MAX_PAGES = 6;
const MAX_GLYPH = 96;
interface Sprite {
  page: number;
  sx: number;
  sy: number;
  w: number;
  h: number;
  /** sprite top-left relative to the pen's pixel */
  ox: number;
  oy: number;
}
interface AtlasPage {
  canvas: AtlasCanvas;
  ctx: Ctx2D;
  x: number;
  y: number;
  rowH: number;
}
let pages: AtlasPage[] = [];
let sprites = new Map<string, Sprite | null>();
const bboxes = new WeakMap<Float32Array, [number, number, number, number]>();

function resetAtlas() {
  pages = [];
  sprites = new Map();
}

function bbox(o: Float32Array): [number, number, number, number] {
  let b = bboxes.get(o);
  if (!b) {
    b = [Infinity, Infinity, -Infinity, -Infinity];
    for (let i = 0; i < o.length; ) {
      const n = o[i] === 2 ? 4 : o[i] === 3 ? 6 : o[i] === 4 ? 0 : 2;
      for (let j = 1; j < n; j += 2) {
        b[0] = Math.min(b[0], o[i + j]);
        b[1] = Math.min(b[1], o[i + j + 1]);
        b[2] = Math.max(b[2], o[i + j]);
        b[3] = Math.max(b[3], o[i + j + 1]);
      }
      i += n + 1;
    }
    bboxes.set(o, b);
  }
  return b;
}

function place(w: number, h: number): { page: number; sx: number; sy: number } | null {
  let p = pages[pages.length - 1];
  if (p && p.x + w > PAGE) (p.x = 0), (p.y += p.rowH + 1), (p.rowH = 0);
  if (!p || p.y + h > PAGE) {
    if (pages.length >= MAX_PAGES) {
      resetAtlas();
      return null;
    }
    const canvas = makeCanvas!(PAGE, PAGE);
    // CPU-backed like the canvases text is rastered into (copying from a GPU canvas into one stalls)
    p = { canvas, ctx: canvas.getContext('2d', { willReadFrequently: true }) as Ctx2D, x: 0, y: 0, rowH: 0 };
    pages.push(p);
  }
  const at = { page: pages.length - 1, sx: p.x, sy: p.y };
  p.x += w + 1;
  p.rowH = Math.max(p.rowH, h);
  return at;
}

interface Part {
  o: Float32Array;
  /** offset from the word's pen pixel origin (device px) */
  dx: number;
  dy: number;
  size: number;
  skew: number;
}

/** A word (or single glyph) rendered once into the atlas, at quarter-pixel offset fx, fy. */
function sprite(parts: Part[], fx: number, fy: number, color: string, key: string): Sprite | null {
  let sp = sprites.get(key);
  if (sp !== undefined) return sp;
  let L = Infinity,
    T = Infinity,
    R = -Infinity,
    B = -Infinity;
  for (const p of parts) {
    const [x0, y0, x1, y1] = bbox(p.o);
    if (x0 > x1) continue;
    const sl = p.skew ? [-p.skew * y0, -p.skew * y1] : [0, 0];
    L = Math.min(L, Math.floor(fx + p.dx + (x0 + Math.min(...sl)) * p.size) - 1);
    R = Math.max(R, Math.ceil(fx + p.dx + (x1 + Math.max(...sl)) * p.size) + 1);
    T = Math.min(T, Math.floor(fy + p.dy + y0 * p.size) - 1);
    B = Math.max(B, Math.ceil(fy + p.dy + y1 * p.size) + 1);
  }
  const w = R - L,
    h = B - T;
  const at = w > 0 && h > 0 && w < PAGE / 2 && h < PAGE / 4 ? place(w, h) : null;
  sp = null;
  if (at) {
    const c = pages[at.page].ctx;
    c.fillStyle = color;
    for (const p of parts) {
      c.setTransform(p.size, 0, -p.skew * p.size, p.size, at.sx + fx + p.dx - L, at.sy + fy + p.dy - T);
      c.fill(glyphPath(p.o));
    }
    sp = { page: at.page, sx: at.sx, sy: at.sy, w, h, ox: L, oy: T };
  }
  sprites.set(key, sp);
  return sp;
}

const SPACE = /\s/;

function drawFromAtlas(ctx: Ctx2D, text: string, sh: ShapedText, shaper: TextShaper, x: number, y: number, ls: number, cps: Uint32Array | null): boolean {
  if (!atlasOn || !makeCanvas || !PathImpl || typeof ctx.fillStyle !== 'string' || typeof ctx.getTransform !== 'function') return false;
  const m = ctx.getTransform();
  if (m.b !== 0 || m.c !== 0 || m.a <= 0 || Math.abs(m.a - m.d) > 1e-9) return false;
  const s = m.a;
  if (sh.glyphs.some((g) => g.size * s > MAX_GLYPH)) return false;
  const color = ctx.fillStyle;
  const draws: [Sprite, number, number][] = [];
  // words (glyphs between spaces) are cached whole: one copy per word instead of one per glyph
  let word: { g: ShapedGlyph; o: Float32Array; X: number; Y: number }[] = [];
  const flush = () => {
    if (!word.length) return true;
    const X0 = word[0].X,
      Y0 = word[0].Y;
    // nearest quarter pixel (rounding down would shift text left and up)
    const qx = Math.round(X0 * 4) / 4,
      qy = Math.round(Y0 * 4) / 4;
    const ix = Math.floor(qx),
      iy = Math.floor(qy);
    const fx = qx - ix,
      fy = qy - iy;
    let key = `${color}|${fx}|${fy}`;
    const parts: Part[] = word.map(({ g, o, X, Y }) => {
      const p = { o, dx: Math.round((X - X0) * 4) / 4, dy: Math.round((Y - Y0) * 4) / 4, size: Math.round(g.size * s * 100) / 100, skew: g.skew };
      key += `|${g.face}.${g.gid}.${p.dx}.${p.dy}.${p.size}.${p.skew}`;
      return p;
    });
    word = [];
    const sp = sprite(parts, fx, fy, color, key);
    // a word too big for a sprite (a long word in a headline), or the atlas was just reset: draw this run as paths
    if (sp === null) return false;
    draws.push([sp, ix + sp.ox, iy + sp.oy]);
    return true;
  };
  for (const g of sh.glyphs) {
    if (SPACE.test(text[g.cluster] ?? '')) {
      if (!flush()) return false;
      continue;
    }
    const o = shaper.outline(g.face, g.gid);
    if (!o) continue;
    word.push({ g, o, X: s * (x + g.x + (cps ? ls * cps[g.cluster] : 0)) + m.e, Y: s * (y + g.y) + m.f });
  }
  if (!flush()) return false;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  for (const [sp, dx, dy] of draws) ctx.drawImage(pages[sp.page].canvas, sp.sx, sp.sy, sp.w, sp.h, dx, dy, sp.w, sp.h);
  ctx.restore();
  return true;
}

/** Without Path2D: trace every glyph's outline into the context. */
function traceGlyphs(ctx: Ctx2D, sh: ShapedText, shaper: TextShaper, x: number, y: number, ls: number, cps: Uint32Array | null) {
  ctx.beginPath();
  for (const g of sh.glyphs) {
    const o = shaper.outline(g.face, g.gid);
    if (!o) continue;
    const k = g.size;
    const ox = x + g.x + (cps ? ls * cps[g.cluster] : 0),
      oy = y + g.y,
      sk = g.skew;
    for (let i = 0; i < o.length; ) {
      switch (o[i]) {
        case 0:
          ctx.moveTo(ox + (o[i + 1] - sk * o[i + 2]) * k, oy + o[i + 2] * k);
          i += 3;
          break;
        case 1:
          ctx.lineTo(ox + (o[i + 1] - sk * o[i + 2]) * k, oy + o[i + 2] * k);
          i += 3;
          break;
        case 2:
          ctx.quadraticCurveTo(ox + (o[i + 1] - sk * o[i + 2]) * k, oy + o[i + 2] * k, ox + (o[i + 3] - sk * o[i + 4]) * k, oy + o[i + 4] * k);
          i += 5;
          break;
        case 3:
          ctx.bezierCurveTo(ox + (o[i + 1] - sk * o[i + 2]) * k, oy + o[i + 2] * k, ox + (o[i + 3] - sk * o[i + 4]) * k, oy + o[i + 4] * k, ox + (o[i + 5] - sk * o[i + 6]) * k, oy + o[i + 6] * k);
          i += 7;
          break;
        default:
          ctx.closePath();
          i += 1;
      }
    }
  }
  ctx.fill();
}

/** The canvas's own text drawing, emulating letter spacing, tabular figures and no-ligatures. */
function drawRunWithCanvas(ctx: Ctx2D, text: string, x: number, y: number, ls: number, font: string, fx?: GlyphFeatures) {
  // a right-to-left run (layout passes one direction at a time): draw it with an RTL base so its
  // spaces and punctuation land on the correct side, anchored at its left edge like any run
  if (RTL_CHARS.test(text) && paragraphLevel(text) === 1 && 'direction' in ctx && ctx.direction !== 'rtl') {
    const [dir, align] = [ctx.direction, ctx.textAlign];
    ctx.direction = 'rtl';
    ctx.textAlign = 'left';
    drawRunWithCanvas(ctx, text, x, y, ls, font, fx);
    ctx.direction = dir;
    ctx.textAlign = align;
    return;
  }
  const runs = smallCapsRuns(text, font);
  if (runs.length > 1 || runs[0][1] !== font) {
    // synthetic small caps: each run in its own font
    for (const [t, f] of runs) {
      ctx.font = f;
      drawRunWithCanvas(ctx, t, x, y, ls, f, fx);
      x += measureString(t, f, ls, fx);
    }
    ctx.font = font;
    return;
  }
  const tnum = !!fx?.tnum;
  if (!ls && !tnum && fx?.liga !== false) return ctx.fillText(text, x, y);
  const cell = tnum ? tabularCell(font) : 0;
  const digit = (ch: string) => ch >= '0' && ch <= '9';
  if (ls || fx?.liga === false) {
    for (const ch of text) {
      const w = ctx.measureText(ch).width;
      if (tnum && digit(ch)) {
        ctx.fillText(ch, x + (cell - w) / 2, y);
        x += cell + ls;
      } else {
        ctx.fillText(ch, x, y);
        x += w + ls;
      }
    }
    return;
  }
  // tabular figures only: words keep their kerning, digits sit centered in equal cells
  for (const seg of text.match(/[0-9]+|[^0-9]+/g) ?? []) {
    if (digit(seg[0]))
      for (const ch of seg) {
        ctx.fillText(ch, x + (cell - ctx.measureText(ch).width) / 2, y);
        x += cell;
      }
    else {
      ctx.fillText(seg, x, y);
      x += ctx.measureText(seg).width;
    }
  }
}

function norm(v: Vec2): Vec2 {
  const l = Math.hypot(v.x, v.y) || 1;
  return { x: v.x / l, y: v.y / l };
}

/** For connectors the stored geometry is in page coordinates; undo the parent's world transform. */
function invertLinear(doc: Document, n: NodeData): Mat {
  const pw = n.parent ? doc.worldTransform(n.parent) : IDENTITY;
  const det = pw[0] * pw[3] - pw[1] * pw[2];
  if (Math.abs(det) < 1e-12) return IDENTITY;
  const id = 1 / det;
  return [pw[3] * id, -pw[1] * id, -pw[2] * id, pw[0] * id, (pw[2] * pw[5] - pw[3] * pw[4]) * id, (pw[1] * pw[4] - pw[0] * pw[5]) * id];
}

export function connectorMidpoint(kind: string, pts: Vec2[]): Vec2 {
  if (kind === 'CURVED') {
    const t = 0.5,
      mt = 0.5;
    return {
      x: mt * mt * mt * pts[0].x + 3 * mt * mt * t * pts[1].x + 3 * mt * t * t * pts[2].x + t * t * t * pts[3].x,
      y: mt * mt * mt * pts[0].y + 3 * mt * mt * t * pts[1].y + 3 * mt * t * t * pts[2].y + t * t * t * pts[3].y,
    };
  }
  let total = 0;
  for (let i = 0; i + 1 < pts.length; i++) total += Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
  let half = total / 2;
  for (let i = 0; i + 1 < pts.length; i++) {
    const l = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
    if (half <= l) return { x: pts[i].x + ((pts[i + 1].x - pts[i].x) * half) / (l || 1), y: pts[i].y + ((pts[i + 1].y - pts[i].y) * half) / (l || 1) };
    half -= l;
  }
  return pts[0];
}

function capInset(cap: NodeData['startCap'], w: number): number {
  if (cap === 'ARROW' || cap === 'TRIANGLE') return Math.max(4, w * 3) * 0.9;
  return 0;
}

/** Draw an end cap at point p facing direction d (unit vector pointing outward). */
export function drawCap(ctx: Ctx2D, cap: NodeData['startCap'], p: Vec2, d: Vec2, w: number) {
  if (!cap || cap === 'NONE' || cap === 'ROUND' || cap === 'SQUARE') return;
  const size = Math.max(5, w * 3.5);
  const nx = -d.y,
    ny = d.x;
  ctx.save();
  ctx.setLineDash([]);
  ctx.beginPath();
  if (cap === 'ARROW') {
    ctx.moveTo(p.x - d.x * size + nx * size * 0.6, p.y - d.y * size + ny * size * 0.6);
    ctx.lineTo(p.x, p.y);
    ctx.lineTo(p.x - d.x * size - nx * size * 0.6, p.y - d.y * size - ny * size * 0.6);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.stroke();
  } else if (cap === 'TRIANGLE') {
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x - d.x * size + nx * size * 0.55, p.y - d.y * size + ny * size * 0.55);
    ctx.lineTo(p.x - d.x * size - nx * size * 0.55, p.y - d.y * size - ny * size * 0.55);
    ctx.closePath();
    ctx.fill();
  } else if (cap === 'CIRCLE') {
    ctx.arc(p.x - d.x * size * 0.4, p.y - d.y * size * 0.4, size * 0.4, 0, Math.PI * 2);
    ctx.fill();
  } else if (cap === 'DIAMOND') {
    const c = { x: p.x - d.x * size * 0.5, y: p.y - d.y * size * 0.5 };
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(c.x + nx * size * 0.4, c.y + ny * size * 0.4);
    ctx.lineTo(p.x - d.x * size, p.y - d.y * size);
    ctx.lineTo(c.x - nx * size * 0.4, c.y - ny * size * 0.4);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

/** Bounds of a set of nodes in page coordinates. */
/**
 * Whether a mask layer masks by its own alpha (Figma's alpha masks): translucent or gradient fills,
 * images, strokes, effects or layer opacity. An opaque fill masks to exactly its outline, and a layer
 * with neither fill nor stroke has only its outline to give (a vector mask): both of those clip.
 */
export function alphaMask(n: NodeData): boolean {
  const fills = n.fills.filter((p) => p.visible !== false);
  const strokes = n.strokes.some((p) => p.visible !== false);
  if (!fills.length && !strokes) return false;
  if (n.opacity < 1 || strokes || n.effects.some((e) => e.visible !== false)) return true;
  return fills.some((p) => {
    const o = p.opacity ?? 1;
    if (p.type === 'SOLID') return o * p.color.a < 1;
    if (isGradient(p)) return o < 1 || p.stops.some((s) => s.color.a < 1);
    return true; // images, patterns and shaders: their pixels decide
  });
}

/**
 * What exporting these layers covers (Figma's render bounds): their boxes, plus what outside or
 * centered strokes, shadows and blurs reach, plus children overflowing containers that don't clip.
 * A frame without those exports at exactly its size.
 */
export function exportBounds(doc: Document, ids: NodeId[]): Rect | null {
  let r: Rect | null = null;
  const add = (n: NodeData) => {
    const b = visualBounds(doc, n, true);
    r = r ? unionR(r, b) : b;
    const container = n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE' || n.type === 'GROUP' || n.type === 'SECTION';
    const clips = (n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE') && n.clipsContent;
    if (container && !clips) for (const c of doc.children(n.id)) if (c.visible && !c.isMask) add(c);
  };
  for (const id of ids) add(doc.must(id));
  return r;
}

export function boundsOfNodes(doc: Document, ids: NodeId[]): Rect | null {
  let r: Rect | null = null;
  for (const id of ids) {
    const b = visualBounds(doc, doc.must(id));
    r = r ? unionR(r, b) : b;
  }
  return r;
}

function unionR(a: Rect, b: Rect): Rect {
  const x = Math.min(a.x, b.x),
    y = Math.min(a.y, b.y);
  return { x, y, width: Math.max(a.x + a.width, b.x + b.width) - x, height: Math.max(a.y + a.height, b.y + b.height) - y };
}

export { transformRect, flattenPath };

function defaultCanvas(w: number, h: number) {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(Math.max(1, w), Math.max(1, h));
  if (typeof document !== 'undefined') {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  }
  return null;
}

type AtlasImage = CanvasImageSource;

function hash2(i: number, seed: number): number {
  let h = Math.imul(i ^ seed, 0x27d4eb2d) ^ 0x165667b1;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  return ((h ^ (h >>> 13)) >>> 0) / 4294967296;
}

/** Smooth 2-D value noise in -1..1. */
function noise2(x: number, y: number, seed: number): number {
  const xi = Math.floor(x),
    yi = Math.floor(y);
  const fx = x - xi,
    fy = y - yi;
  const u = fx * fx * (3 - 2 * fx),
    v = fy * fy * (3 - 2 * fy);
  const h = (a: number, b: number) => hash2(a * 374761393 + b * 668265263, seed);
  const top = h(xi, yi) * (1 - u) + h(xi + 1, yi) * u;
  const bottom = h(xi, yi + 1) * (1 - u) + h(xi + 1, yi + 1) * u;
  return (top * (1 - v) + bottom * v) * 2 - 1;
}

function hsl(h: number, s: number, l: number): Color {
  const k = (n: number) => (n + h * 12) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
  return { r: f(0), g: f(8), b: f(4), a: 1 };
}
